import { Injectable } from '@angular/core';
import Parse from 'parse';
import {
  AnalysisStateTopology,
  CaseTopology,
  ParseBaseRepository,
  SampleTopology,
} from '@telespot/infrastructure/parse';
import { IAnalysisState } from '@analysis/domain';
import { type AnalysisStateRepository } from '@analysis/use-cases';
import { TelespotAnalysisStateMapper } from './telespot-analysis-state.mapper';
import { ApiAuthErrors } from '@telespot/web-core';

@Injectable({
  providedIn: 'root',
})
export class TelespotAnalysisStateRepository extends ParseBaseRepository implements AnalysisStateRepository {
  private readonly mapper = new TelespotAnalysisStateMapper(Parse);

  constructor() {
    super(Parse, { useMasterKey: false });
  }

  public async fetchAnalysisStates(sampleId: string): Promise<IAnalysisState[]> {
    const statesBySample = await this.fetchAnalysisStatesForSamples([sampleId]);

    return statesBySample.get(sampleId) ?? [];
  }

  public async fetchAnalysisStatesForSamples(sampleIds: string[]): Promise<Map<string, IAnalysisState[]>> {
    const statesBySample = new Map<string, IAnalysisState[]>();

    if (sampleIds.length === 0) {
      return statesBySample;
    }

    const samplePointers = sampleIds.map((id) =>
      this.subclasses.getSubclass(SampleTopology.TABLE).createWithoutData(id)
    );

    const query = new this.parse.Query(AnalysisStateTopology.TABLE)
      .containedIn(AnalysisStateTopology.SAMPLE, samplePointers)
      .include(AnalysisStateTopology.USER, AnalysisStateTopology.ALGORITHM, AnalysisStateTopology.SAMPLE);

    try {
      const parseObjects = await query.find();

      for (const object of parseObjects) {
        const sampleId = object.get(AnalysisStateTopology.SAMPLE)?.id;
        if (!sampleId) continue;

        const analysisState = TelespotAnalysisStateMapper.toDomain(object);
        if (!analysisState) continue;

        const sampleStates = statesBySample.get(sampleId) ?? [];
        sampleStates.push(analysisState);
        statesBySample.set(sampleId, sampleStates);
      }

      return statesBySample;
    } catch (error) {
      throw this.handleAuthError(error, ApiAuthErrors.UNAUTHORIZED);
    }
  }

  async getUserState(userId: string, caseId: string, sampleId?: string): Promise<IAnalysisState | null> {
    const userReference = this.parse.User.createWithoutData(userId);

    const query = new this.parse.Query(AnalysisStateTopology.TABLE)
      .include(AnalysisStateTopology.USER, AnalysisStateTopology.ALGORITHM, AnalysisStateTopology.SAMPLE)
      .equalTo(AnalysisStateTopology.USER, userReference);

    if (caseId) {
      const caseReference = this.subclasses.getSubclass(CaseTopology.TABLE).createWithoutData(caseId);
      query.equalTo(AnalysisStateTopology.CASE, caseReference);
    }

    if (sampleId) {
      const sampleRef = this.subclasses.getSubclass(SampleTopology.TABLE).createWithoutData(sampleId);
      query.equalTo(AnalysisStateTopology.SAMPLE, sampleRef);
    } else {
      query.doesNotExist(AnalysisStateTopology.SAMPLE);
    }

    try {
      const parseObject = await query.first();
      if (!parseObject) return null;
      return TelespotAnalysisStateMapper.toDomain(parseObject);
    } catch (error) {
      throw this.handleAuthError(error, ApiAuthErrors.UNAUTHORIZED);
    }
  }

  async saveAnalysisState(analysisState: IAnalysisState, sampleId: string, caseId: string): Promise<IAnalysisState> {
    try {
      const parseObject = this.mapper.fromDomain(analysisState, sampleId, caseId);
      const savedParseObject = await parseObject.save();
      const saved = TelespotAnalysisStateMapper.toDomain(savedParseObject);
      if (!saved) {
        throw new Error('Saved AnalysisState is missing user or algorithm');
      }
      return saved;
    } catch (error) {
      throw this.handleAuthError(error, ApiAuthErrors.UNAUTHORIZED);
    }
  }

  async getUserSampleStatuses(userId: string, sampleIds: string[]): Promise<Map<string, IAnalysisState>> {
    const userReference = this.parse.User.createWithoutData(userId);
    const samplePointers = sampleIds.map((id) =>
      this.subclasses.getSubclass(SampleTopology.TABLE).createWithoutData(id)
    );

    const query = new this.parse.Query(AnalysisStateTopology.TABLE)
      .equalTo(AnalysisStateTopology.USER, userReference)
      .containedIn(AnalysisStateTopology.SAMPLE, samplePointers)
      .include(AnalysisStateTopology.SAMPLE);

    try {
      const parseObjects = await query.find();
      const statusMap = new Map<string, IAnalysisState>();

      for (const obj of parseObjects) {
        const sampleId = obj.get(AnalysisStateTopology.SAMPLE)?.id;
        if (!sampleId) continue;

        const analysisState = TelespotAnalysisStateMapper.toDomain(obj);
        if (analysisState) {
          statusMap.set(sampleId, analysisState);
        }
      }

      return statusMap;
    } catch (error) {
      throw this.handleAuthError(error, ApiAuthErrors.UNAUTHORIZED);
    }
  }
}
